"use client";

import { activeSiteConfig } from "@config/site/site-config";
import { CommonCMS } from "@lib/constants/contentful";
import { ExtraPriceType } from "@lib/constants/filtersConstants";
import { useMicrocopy } from "@lib/contentful/microcopy/MicrocopyContext";
import { isSalePriceHighlighted } from "@lib/utils/productUtility";
import { Text } from "@ecco/ui/text";
import { Money } from "@ecco/ui/money";
import { Flex } from "@ecco/ui/flex";
import { useLocale } from "next-intl";
import { ProductTileTheme } from "../ProductTile.styles";
import { ProductTilePriceStyles as styles } from "./ProductTilePrice.styles";
import type { Price } from "@lib/wrappers/algolia/types";
import { useMoney } from "@ecco/ui/use-money";

type ProductTilePrice = {
    currentPrice?: Price;
    recommendedPrice?: Price;
    previousLowestPrice?: Price;
    discountPercentage?: number;
    discountPercentagePreviousLowest?: number;
};

interface ProductTilePriceProps {
    price: ProductTilePrice;
    isExpanded?: boolean;
    theme?: string;
    isDiscounted?: boolean;
}

interface OmnibusConfig {
    isPriceOnlyOmnibus: boolean;
    hasOmnibusPrice: boolean;
    hasRecommendedPrice: boolean;
    effectiveOmnibusPrice?: Price;
    effectiveOmnibusDiscount?: number;
}

function buildOmnibusConfig(
    price: ProductTilePrice,
    isDiscounted: boolean,
    omnibusTemplate: string
): OmnibusConfig {
    const {
        currentPrice,
        previousLowestPrice,
        recommendedPrice,
        discountPercentage,
        discountPercentagePreviousLowest,
    } = price;

    const hasPreviousLowestPrice = !!previousLowestPrice?.centAmount;

    const effectiveOmnibusPrice = hasPreviousLowestPrice ? previousLowestPrice : recommendedPrice;

    const effectiveOmnibusDiscount = hasPreviousLowestPrice
        ? discountPercentagePreviousLowest
        : discountPercentage;

    const isPriceOnlyOmnibus = omnibusTemplate?.trim() === "{{price}}";

    const currentCentAmount = currentPrice?.centAmount ?? 0;

    const hasOmnibusPrice =
        isDiscounted &&
        !!effectiveOmnibusPrice?.centAmount &&
        effectiveOmnibusPrice.centAmount > currentCentAmount;

    const hasRecommendedPrice =
        isDiscounted &&
        !!recommendedPrice?.centAmount &&
        recommendedPrice.centAmount > currentCentAmount;

    return {
        effectiveOmnibusPrice,
        effectiveOmnibusDiscount,
        isPriceOnlyOmnibus,
        hasOmnibusPrice,
        hasRecommendedPrice,
    };
}

function resolveStrikePrice(
    isDiscounted: boolean,
    isOmnibusMarket: boolean,
    recommendedPrice?: Price,
    omnibusConfig?: OmnibusConfig
): Price {
    if (!isDiscounted) return;
    if (!isOmnibusMarket) return recommendedPrice;
    if (omnibusConfig?.isPriceOnlyOmnibus) return omnibusConfig.effectiveOmnibusPrice;
    return;
}

interface PriceNoteProps {
    children: React.ReactNode;
    isExpanded?: boolean;
}

const PriceNote = ({ children, isExpanded }: PriceNoteProps) => (
    <Flex
        direction="column"
        gap={1}
        mt={isExpanded ? 0 : 3}
        fontSize={["desktopBodyTextSmall", "desktopBodyTextNormal"]}
    >
        <Text as="div" sx={styles.omnibusWrapper}>
            {children}
        </Text>
    </Flex>
);

export const ProductTilePrice = ({
    price,
    isExpanded,
    theme,
    isDiscounted = false,
}: ProductTilePriceProps) => {
    const locale = useLocale();
    const { get } = useMicrocopy();

    const themeConfig = ProductTileTheme[theme || ""] || {};
    const themeText = themeConfig.text;

    const { currentPrice, recommendedPrice } = price;

    const isOmnibusMarket =
        activeSiteConfig?.extraPriceType?.includes(ExtraPriceType.OMNIBUS) ?? false;
    const isRRPMarket = activeSiteConfig?.extraPriceType?.includes(ExtraPriceType.RRP) ?? false;

    // Build omnibus config only when the market requires it
    const omnibusTemplate = isOmnibusMarket
        ? (get(CommonCMS.shared, CommonCMS.price.omnibusPrice, {
              replacements: {},
              jsx: false,
          }) as string)
        : "";

    const omnibusConfig = isOmnibusMarket
        ? buildOmnibusConfig(price, isDiscounted, omnibusTemplate)
        : null;

    const strikePrice = resolveStrikePrice(
        isDiscounted,
        isOmnibusMarket,
        recommendedPrice,
        omnibusConfig
    );

    const strikePriceObj = useMoney({
        money: strikePrice,
        locale,
    });

    const priceColor = isSalePriceHighlighted({
        isDiscounted,
        omnibusDiscountPercentage: omnibusConfig?.effectiveOmnibusDiscount,
    })
        ? "red.100"
        : (themeText?.color ?? "black");

    const omnibusMicrocopy =
        omnibusConfig?.hasOmnibusPrice && !omnibusConfig.isPriceOnlyOmnibus
            ? get(CommonCMS.shared, CommonCMS.price.omnibusPrice, {
                  replacements: {
                      price: (
                          <Money
                              data={omnibusConfig.effectiveOmnibusPrice}
                              locale={locale}
                              textDecoration={
                                  omnibusConfig.effectiveOmnibusDiscount
                                      ? "line-through"
                                      : undefined
                              }
                              style={{ display: "inline" }}
                          />
                      ),
                      discount:
                          (omnibusConfig.effectiveOmnibusDiscount ?? 0) > 0 ? (
                              <Text sx={styles.omnibusDiscount}>
                                  -{omnibusConfig.effectiveOmnibusDiscount}%
                              </Text>
                          ) : null,
                  },
                  jsx: true,
              })
            : null;

    const recommendedMicrocopy =
        isRRPMarket &&
        omnibusConfig?.hasRecommendedPrice &&
        !omnibusConfig.isPriceOnlyOmnibus &&
        (recommendedPrice?.centAmount ?? 0) > (currentPrice?.centAmount ?? 0)
            ? get(CommonCMS.shared, CommonCMS.price.recommendedPrice, {
                  replacements: {
                      price: (
                          <Money
                              data={recommendedPrice}
                              locale={locale}
                              textDecoration="line-through"
                              style={{ display: "inline" }}
                          />
                      ),
                  },
                  jsx: true,
              })
            : null;

    return (
        <>
            {/* Current price + optional strike price */}
            <Flex
                direction="row"
                gap={1}
                mt={isExpanded ? 0 : 3}
                fontSize={["desktopBodyTextSmall", "desktopBodyTextNormal"]}
            >
                <Money
                    data={currentPrice}
                    locale={locale}
                    color={priceColor}
                    fontWeight="bold"
                    className="product-price"
                    {...(theme && !isDiscounted ? themeText : {})}
                />

                {strikePrice && (
                    <Money
                        data={strikePrice}
                        locale={locale}
                        fontWeight="bold"
                        color="gray.500"
                        textDecoration="line-through"
                        className="recommended-retail-price"
                        ariaLabel={get(CommonCMS.shared, CommonCMS.price.recommendedPrice, {
                            replacements: {
                                price: strikePriceObj.localizedString,
                            },
                            jsx: true,
                        })}
                        {...themeConfig.textGray}
                    />
                )}
            </Flex>

            {/* Omnibus and RRP price */}
            {omnibusMicrocopy && <PriceNote isExpanded={isExpanded}>{omnibusMicrocopy}</PriceNote>}
            {recommendedMicrocopy && (
                <PriceNote isExpanded={isExpanded}>{recommendedMicrocopy}</PriceNote>
            )}
        </>
    );
};
